/**
 * @file     ConversionCTA.tsx
 * @readme   ../README.md
 * @purpose  Next-step links at the end of a lesson: a hairline link list under a micro label, chosen by audience (mortgage calculator, deal finder, neighbourhoods, home value, tenants).
 */
'use client';

import '@/core/i18n/deferred/academy';
import Box from '@mui/material/Box';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import type { AudienceId } from '../types';
import { useAuth } from '@/hooks/useAuth';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { focusRingSx, hairline, hubColors, microLabelSx, reducedMotionSx, transition } from '@/components/hub/hubKitStyles';
import {
  DEAL_FINDER_SUBMITTED_DESTINATION,
  DEAL_FINDER_SUBMITTED_LABEL,
  isDealFinderFunnelPath,
} from '@/core/config/ctaState';

export interface ConversionLink {
  key: string;
  title: string;
  description: string;
  href: string;
}

export const CONVERSION_LINKS: Record<AudienceId, ConversionLink[]> = {
  buyer: [
    { key: 'mortgage', title: 'Run the mortgage numbers', description: 'Mortgage calculator', href: '/mortgage-calculator' },
    { key: 'shortlist', title: 'Get a shortlist of homes', description: 'A few questions, then current listings', href: '/deal-finder' },
    {
      key: 'neighbourhood',
      title: 'Find your neighbourhood',
      description: 'Vancouver neighbourhoods ranked by what matters to you',
      href: '/find-your-neighbourhood-vancouver',
    },
  ],
  seller: [
    { key: 'homeValue', title: 'Get a home value report', description: 'A price range for your home', href: '/sell-house' },
    { key: 'sellVancouver', title: 'See the Vancouver market', description: 'Homes for sale that compete with yours', href: '/sell-house/vancouver' },
  ],
  landlord: [
    { key: 'tenants', title: 'Find tenants', description: 'Tenant placement in Vancouver', href: '/deal-finder/landlord' },
    {
      key: 'neighbourhood',
      title: 'Compare neighbourhoods',
      description: 'Vancouver neighbourhoods ranked by what matters to you',
      href: '/find-your-neighbourhood-vancouver',
    },
  ],
};

const ArrowGlyph = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M7 17L17 7M9 7h8v8" />
  </svg>
);

interface ConversionCTAProps {
  audienceId: AudienceId;
}

export function ConversionCTA({ audienceId }: ConversionCTAProps) {
  const { t } = useTranslation();
  const { hasSubmittedDealFinder } = useAuth();
  const links = CONVERSION_LINKS[audienceId].map((c) => {
    const base = {
      ...c,
      title: t(`academy.cta.${audienceId}.${c.key}.title`, { defaultValue: c.title }),
      description: t(`academy.cta.${audienceId}.${c.key}.description`, { defaultValue: c.description }),
    };
    return hasSubmittedDealFinder && isDealFinderFunnelPath(c.href)
      ? {
          ...base,
          href: DEAL_FINDER_SUBMITTED_DESTINATION,
          title: t('academy.cta.submitted.title', { defaultValue: DEAL_FINDER_SUBMITTED_LABEL }),
          description: t('academy.cta.submitted.description', { defaultValue: 'Your home matches' }),
        }
      : base;
  });

  return (
    <Box component="section" aria-labelledby="lesson-next-steps">
      <Box component="h2" id="lesson-next-steps" sx={{ ...microLabelSx, mb: 1.5 }}>
        {t('academy.cta.heading', { defaultValue: 'Put it to work' })}
      </Box>
      <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', borderTop: hairline }}>
        {links.map((link) => (
          <Box component="li" key={link.key} sx={{ borderBottom: hairline }}>
            <Box
              component={Link}
              href={link.href}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                minHeight: 56,
                py: 1.5,
                textDecoration: 'none',
                color: hubColors.textPrimary,
                transition: transition('color'),
                '& [data-arrow]': { color: hubColors.textMuted, transition: transition('color') },
                '@media (hover: hover)': {
                  '&:hover': { color: hubColors.link },
                  '&:hover [data-arrow]': { color: hubColors.link },
                },
                ...focusRingSx,
                ...reducedMotionSx,
              }}
            >
              <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
                <Box component="span" sx={{ fontSize: 15, fontWeight: fontWeightTokens.medium, lineHeight: 1.35 }}>
                  {link.title}
                </Box>
                <Box component="span" sx={{ fontSize: 13, lineHeight: 1.4, color: hubColors.textSecondary }}>
                  {link.description}
                </Box>
              </Box>
              <Box component="span" data-arrow sx={{ display: 'inline-flex', flexShrink: 0 }}>
                <ArrowGlyph />
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
